import React from 'react';
import '../pages-styles/PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="privacy-container">
      <div className="privacy-content">
        <h1>Privacy Policy</h1>
        <p className="subtitle">Magbity Agency</p>
        <p className="last-updated">Last updated: January 26, 2026</p>

        <section>
          <p>
            Magbity Agency ("we", "us", "our") takes data protection seriously. This Privacy Policy explains how 
            personal data is collected, processed, stored, and protected when using our website, digital platforms, 
            consultancy services, and football agency services.
          </p>
          <p>
            <strong>By using our services, you confirm that you have read, understood, and accepted this Privacy Policy.</strong>
          </p>
        </section>

        <section>
          <h2>1. Data Controller</h2>
          <p>
            Magbity Agency is the Data Controller for all personal data processed.
          </p>
          <p>
            <strong>Contact:</strong><br />
            <a href="mailto:info@magbityagencyltd.com">info@magbityagency.com</a><br />
            <a href="https://www.magbityagency.com">www.magbityagency.com</a>
          </p>
          <p>
            All data protection requests must be submitted in writing to the above contact.
          </p>
        </section>

        <section>
          <h2>2. Scope of This Policy</h2>
          <p>
            This policy applies to:
          </p>
          <ul>
            <li>Website and web application users</li>
            <li>Consultancy clients</li>
            <li>Football players (professional and youth)</li>
            <li>Parents or legal guardians</li>
            <li>Business partners and representatives</li>
          </ul>
          <p>
            <strong>If you do not agree with this policy, you must not use our services.</strong>
          </p>
        </section>

        <section>
          <h2>3. Personal Data We Collect</h2>
          <p>
            We collect only data that is necessary for business, contractual, regulatory, or legal purposes.
          </p>
          <p>
            <strong>Categories include:</strong>
          </p>
          <ul>
            <li>Identity data (name, DOB, nationality)</li>
            <li>Contact data (email, phone, address)</li>
            <li>Professional and career information</li>
            <li>Contractual and representation data</li>
            <li>Communications and correspondence</li>
            <li>Technical data (IP address, device, logs)</li>
            <li>Legal, compliance, and regulatory records</li>
          </ul>
          <p>
            We do not accept responsibility for incorrect or misleading data supplied by users or third parties.
          </p>
        </section>

        <section>
          <h2>4. How Data Is Collected</h2>
          <p>
            Data is collected when:
          </p>
          <ul>
            <li>You submit forms, enquiries, or documents</li>
            <li>You enter into contracts or representation agreements</li>
            <li>You communicate with us</li>
            <li>You use our website or digital platforms</li>
            <li>Data is lawfully received from clubs, governing bodies, or partners</li>
          </ul>
        </section>

        <section>
          <h2>5. Lawful Basis for Processing</h2>
          <p>
            We process personal data under the following lawful bases:
          </p>
          <ul>
            <li>Contractual necessity</li>
            <li>Legal and regulatory obligation</li>
            <li>Legitimate business interests</li>
            <li>Explicit consent (where required)</li>
          </ul>
          <p>
            Where processing is required to comply with football regulations or law, consent is not required and 
            cannot be withdrawn.
          </p>
        </section>

        <section>
          <h2>6. Purpose of Data Processing</h2>
          <p>
            Your data may be used to:
          </p>
          <ul>
            <li>Deliver consultancy and digital services</li>
            <li>Represent football players and liaise with clubs</li>
            <li>Conduct negotiations and regulatory filings</li>
            <li>Communicate professionally and contractually</li>
            <li>Prevent fraud, abuse, or misconduct</li>
            <li>Protect legal rights and enforce agreements</li>
            <li>Meet regulatory, audit, and compliance obligations</li>
          </ul>
        </section>

        <section>
          <h2>7. Data Sharing & Disclosure</h2>
          <p>
            We may share personal data only where necessary with:
          </p>
          <ul>
            <li>Football clubs, leagues, and governing bodies</li>
            <li>Legal, regulatory, or disciplinary authorities</li>
            <li>Professional advisers and auditors</li>
            <li>Hosting, IT, and security providers</li>
          </ul>
          <p>
            <strong>We do not sell personal data.</strong>
          </p>
          <p>
            We reserve the right to disclose data without notice where required by law or to protect our legal interests.
          </p>
        </section>

        <section>
          <h2>8. International Data Transfers</h2>
          <p>
            Where data is transferred outside the UK, we ensure appropriate safeguards in line with UK GDPR. Use of 
            our services constitutes acceptance of such transfers.
          </p>
        </section>

        <section>
          <h2>9. Data Retention</h2>
          <p>
            We retain personal data:
          </p>
          <ul>
            <li>For the duration of contractual relationships</li>
            <li>As required by football regulations and law</li>
            <li>For legal defence, dispute resolution, and compliance</li>
          </ul>
          <p>
            Deletion requests will not apply where retention is legally or contractually required.
          </p>
        </section>

        <section>
          <h2>10. Data Security</h2>
          <p>
            We apply strict technical and organisational measures, including access controls, monitoring, and 
            secure storage.
          </p>
          <p>
            <strong>However, no system is 100% secure. Use of our platforms is at your own risk.</strong>
          </p>
        </section>

        <section>
          <h2>11. User Responsibilities</h2>
          <p>
            You agree:
          </p>
          <ul>
            <li>To provide accurate and lawful data</li>
            <li>Not to submit false, misleading, or malicious requests</li>
            <li>Not to misuse data access rights</li>
          </ul>
          <p>
            Abusive, excessive, or bad-faith requests may be refused or delayed as permitted by law.
          </p>
        </section>

        <section>
          <h2>12. Your Data Rights (UK GDPR)</h2>
          <p>
            You may request:
          </p>
          <ul>
            <li>Access to your data</li>
            <li>Correction of inaccuracies</li>
            <li>Restriction of processing</li>
            <li>Objection where legally applicable</li>
          </ul>
          <p>
            <strong>All requests:</strong>
          </p>
          <ul>
            <li>Must be submitted in writing</li>
            <li>Require identity verification</li>
            <li>May be refused where legally permitted</li>
            <li>Will be responded to within statutory timeframes</li>
          </ul>
        </section>

        <section>
          <h2>13. Complaints & Disputes</h2>
          <p>
            Any concerns must first be raised directly with Magbity Agency.
          </p>
          <p>
            If unresolved, you may contact the UK Information Commissioner's Office (ICO).
          </p>
          <p>
            We reserve the right to defend all claims robustly.
          </p>
        </section>

        <section>
          <h2>14. Cookies & Tracking</h2>
          <p>
            We use cookies and analytics tools for functionality, security, and performance. Continued use of our 
            website constitutes acceptance.
          </p>
        </section>

        <section>
          <h2>15. Policy Updates</h2>
          <p>
            We may amend this policy at any time. Changes take effect immediately upon publication.
          </p>
        </section>

        <section>
          <h2>16. Governing Law</h2>
          <p>
            This Privacy Policy is governed by the laws of England and Wales. Any disputes shall be handled 
            exclusively within this jurisdiction.
          </p>
        </section>

        <section>
          <h2>17. Contact</h2>
          <p>
            <strong>Magbity Agency</strong><br />
            <a href="mailto:info@magbityagency.com">info@magbityagency.com</a><br />
            <a href="https://www.magbityagency.com">www.magbityagency.com</a>
          </p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
